import React from 'react';
import { TextInput, NumberInput, Switch, Textarea, Stack } from '@mantine/core';
import type { NodeRegistryEntry } from '../../types';
import type { ResolvedType } from '../../utils/resolveCodeBlockTypes';
import { CodeEditor } from './CodeEditor';

export type ValuesEditorProps = {
  values: Record<string, unknown>;
  registryEntry: NodeRegistryEntry | null;
  /** Resolved input types connected via edges (for CodeEditor type inference) */
  resolvedInputs?: ResolvedType[];
  /** Resolved output types configured for CodeBlock (for CodeEditor type inference) */
  resolvedOutputs?: ResolvedType[];
  onValuesChange: ( values: Record<string, unknown> ) => void;
  theme?: 'light' | 'dark';
};

/**
 * Determine the appropriate label for a value key.
 */
function formatLabel( key: string ): string {
  return key.charAt( 0 ).toUpperCase() + key.slice( 1 ).replace( /([A-Z])/g, ' $1' );
}

/**
 * Check whether a value should render as a JSON textarea.
 */
function isObjectValue( key: string, value: unknown ): boolean {
  if ( key === 'schema' ) return true;
  return typeof value === 'object' && value !== null;
}

/**
 * Generates form fields from node values based on their types.
 * - string -> TextInput
 * - number -> NumberInput
 * - boolean -> Switch
 * - key "code" -> Textarea (Monaco placeholder)
 * - key "schema" or object -> Textarea with JSON
 * - key "property" -> TextInput
 * - key "formula" -> TextInput
 */
export function ValuesEditor({ values, registryEntry, resolvedInputs, resolvedOutputs, onValuesChange, theme }: ValuesEditorProps ) {
  const isCodeBlock = registryEntry?.name === 'CodeBlock';
  const handleChange = React.useCallback(( key: string, newValue: unknown ) => {
    onValuesChange({ ...values, [key]: newValue });
  }, [ values, onValuesChange ]);

  const entries = Object.entries( values ).filter(
    ([key]) => !( isCodeBlock && key === 'outputs' )
  );

  if ( entries.length === 0 ) {
    return (
      <div style={{ padding: '16px 0', textAlign: 'center' }}>
        <span style={{ color: 'var(--drawer-dimmed)', fontSize: 12 }}>
          This node has no configurable values.
        </span>
      </div>
    );
  }

  return (
    <Stack gap="sm">
      {entries.map(([ key, value ]) => {
        const label = formatLabel( key );

        // Key-specific overrides first
        if ( key === 'code' ) {
          // CodeBlock nodes get the full Monaco editor with TypeScript support
          if ( isCodeBlock ) {
            return (
              <CodeEditor
                key={key}
                value={typeof value === 'string' ? value : JSON.stringify( value, null, 2 )}
                resolvedInputs={resolvedInputs}
                resolvedOutputs={resolvedOutputs}
                onChange={( newCode ) => handleChange( key, newCode )}
                theme={theme}
              />
            );
          }

          // Other node types with a "code" field get a plain textarea
          return (
            <Textarea
              key={key}
              label={label}
              value={typeof value === 'string' ? value : JSON.stringify( value, null, 2 )}
              onChange={( e ) => handleChange( key, e.currentTarget.value )}
              autosize
              minRows={4}
              maxRows={12}
              styles={{
                label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 },
                input: {
                  fontFamily: 'monospace',
                  fontSize: 12,
                  background: 'var(--drawer-input-bg)',
                  borderColor: 'var(--drawer-input-border)',
                  color: 'var(--drawer-text)'
                }
              }}
            />
          );
        }

        if ( key === 'property' || key === 'formula' ) {
          return (
            <TextInput
              key={key}
              label={label}
              value={typeof value === 'string' ? value : String( value ?? '' )}
              onChange={( e ) => handleChange( key, e.currentTarget.value )}
              styles={{
                label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 },
                input: {
                  background: 'var(--drawer-input-bg)',
                  borderColor: 'var(--drawer-input-border)',
                  color: 'var(--drawer-text)'
                }
              }}
            />
          );
        }

        if ( isObjectValue( key, value )) {
          return (
            <Textarea
              key={key}
              label={label}
              value={typeof value === 'string' ? value : JSON.stringify( value, null, 2 )}
              onChange={( e ) => {
                try {
                  const parsed = JSON.parse( e.currentTarget.value );
                  handleChange( key, parsed );
                } catch {
                  // Keep raw string while user is editing invalid JSON
                  handleChange( key, e.currentTarget.value );
                }
              }}
              autosize
              minRows={3}
              maxRows={10}
              styles={{
                label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 },
                input: {
                  fontFamily: 'monospace',
                  fontSize: 12,
                  background: 'var(--drawer-input-bg)',
                  borderColor: 'var(--drawer-input-border)',
                  color: 'var(--drawer-text)'
                }
              }}
            />
          );
        }

        // Type-based rendering
        if ( typeof value === 'boolean' ) {
          return (
            <Switch
              key={key}
              label={label}
              checked={value}
              onChange={( e ) => handleChange( key, e.currentTarget.checked )}
              styles={{
                label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 }
              }}
            />
          );
        }

        if ( typeof value === 'number' ) {
          return (
            <NumberInput
              key={key}
              label={label}
              value={value}
              onChange={( val ) => handleChange( key, val )}
              styles={{
                label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 },
                input: {
                  background: 'var(--drawer-input-bg)',
                  borderColor: 'var(--drawer-input-border)',
                  color: 'var(--drawer-text)'
                }
              }}
            />
          );
        }

        // Default: string TextInput
        return (
          <TextInput
            key={key}
            label={label}
            value={typeof value === 'string' ? value : String( value ?? '' )}
            onChange={( e ) => handleChange( key, e.currentTarget.value )}
            styles={{
              label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 },
              input: {
                background: 'var(--drawer-input-bg)',
                borderColor: 'var(--drawer-input-border)',
                color: 'var(--drawer-text)'
              }
            }}
          />
        );
      })}
    </Stack>
  );
}
